import { Answer, Enrolment } from "../../../stores/EnrolmentStore/Types";
import {
  QuestionPreview,
  TopicPreview,
} from "../../../stores/CoursePreviewStore/Types";
import { CourseOverviewState } from "./types";

export const CIRCLE_LENGTH = 163.36;

export const questionPath = (
  enrolmentId: number,
  question: QuestionPreview,
  answers: Answer[],
) => {
  const answer = answers.find((item) => item.question_id == question?.id);

  if (
    answer &&
    question.kind === "Opinion" &&
    ["pending", "passed"].includes(answer.state)
  ) {
    return `/enrolments/${enrolmentId}/questions/${question.id}/answers/${answer.id}/votes`;
  }

  return `/enrolments/${enrolmentId}/questions/${question.id}/answer`;
};

export const translatedName = (
  item: { name: string; translations?: any[]; topic_translations?: any[] },
  locale?: string,
) => {
  const translations = item.translations || item.topic_translations || [];
  const translation = translations.find((entry) => entry.language === locale);

  return translation?.name || item.name;
};

export const translatedDescription = (
  item: { description: string; topic_translations?: any[] },
  locale?: string,
) => {
  const translation = (item.topic_translations || []).find(
    (entry) => entry.language === locale,
  );

  return translation?.description || item.description;
};

export const minutesLabel = (minutes?: number) => {
  if (!minutes) {
    return "";
  }

  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
};

export const getCourseOverviewState = (
  enrolment: Enrolment,
): CourseOverviewState => {
  if (enrolment?.state === "passed" || enrolment?.percentage_completed >= 100) {
    return "complete";
  }

  if (enrolment?.started || enrolment?.percentage_completed > 0) {
    return "progress";
  }

  return "fresh";
};

export const normalizedNextQuestionPath = (enrolment: Enrolment) => {
  if (!enrolment?.next_question_path) {
    return null;
  }

  return enrolment.next_question_path.endsWith("/answer")
    ? enrolment.next_question_path
    : `${enrolment.next_question_path}/answer`;
};

export const findCurrentLesson = (
  enrolment: Enrolment,
  chapters: TopicPreview[],
  answers: Answer[],
) => {
  if (enrolment?.next_question) {
    return enrolment.next_question;
  }

  const nextPath = normalizedNextQuestionPath(enrolment);
  if (!nextPath) {
    return null;
  }

  return chapters
    .reduce(
      (questions, chapter) => questions.concat(chapter.questions),
      [] as QuestionPreview[],
    )
    .find((question) => {
      return nextPath === questionPath(enrolment?.id, question, answers);
    });
};

export const lessonState = (
  enrolment: Enrolment,
  question: QuestionPreview,
  answers: Answer[],
) => {
  const path = questionPath(enrolment?.id, question, answers);
  const answer = answers.find((item) => item.question_id == question.id);

  if (
    enrolment?.next_question_path === path ||
    normalizedNextQuestionPath(enrolment) === path
  ) {
    return "current";
  }

  if (!answer) {
    return "todo";
  }

  if (["pending", "submitted"].includes(answer.state)) {
    return "await";
  }

  if (answer.state === "failed") {
    return "failed";
  }

  if (answer.state === "passed") {
    return "passed";
  }

  return "todo";
};

export const lessonStatusLabel = (state: string) => {
  switch (state) {
    case "await":
      return "Submitted, awaiting marking.";
    case "current":
      return "Your current lesson.";
    case "failed":
      return "Lesson failed.";
    case "passed":
      return "Passed.";
    default:
      return "Not started.";
  }
};

export const mastheadImage = (enrolment: Enrolment, currentUser: any) => {
  const course = enrolment?.course;

  if (currentUser?.is_in_uk && course?.preview_masthead_alt_image_url) {
    return course.preview_masthead_alt_image_url;
  }

  return (
    course?.preview_masthead_image_url || course?.preview_masthead_alt_image_url
  );
};
